/**
 * Paged carousel for the PDP complementary ("Complete Your Build") block.
 *
 * Each page holds a vertical stack of add-on rows. Arrows and dashes page
 * between them; the active dash expands in width. Rows also report how many
 * of that product are already in the cart, refreshed on cart updates.
 *
 * With data-hide-in-cart (the cart's "Complete your ride" block), rows whose
 * product is in the cart are removed instead of marked, and the host marked
 * data-carousel-host is removed when no rows remain.
 */
if (!customElements.get('e13-addon-carousel')) {
  customElements.define(
    'e13-addon-carousel',
    class E13AddonCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.addon-carousel__track');
        if (!this.track) return;

        this.pages = Array.from(this.track.children);
        this.dashes = Array.from(this.querySelectorAll('.addon-carousel__dash'));
        this.prev = this.querySelector('.addon-carousel__arrow--prev');
        this.next = this.querySelector('.addon-carousel__arrow--next');
        this.index = 0;

        this.onScroll = this.onScroll.bind(this);
        this.refreshCart = this.refreshCart.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.dashes.forEach((dash) => {
          dash.addEventListener('click', () => this.goTo(Number(dash.dataset.index)));
        });
        if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
        if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

        this.setActive(0);
        this.refreshCart();

        // product-form.js publishes cartUpdate after every successful add,
        // including from the quick-add modal, so this is the only refresh needed.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refreshCart);
        }
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.unsubscribe) this.unsubscribe();
      }

      /** Distance between page starts; equals the track width for full-width
       *  pages and the slide pitch for the peek layout. */
      pageStep() {
        if (this.pages.length > 1) return this.pages[1].offsetLeft - this.pages[0].offsetLeft;
        return this.track.clientWidth;
      }

      goTo(index) {
        const clamped = Math.max(0, Math.min(index, this.pages.length - 1));
        if (!this.pages[clamped]) return;
        this.track.scrollTo({
          left: clamped * this.pageStep(),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        this.setActive(clamped);
      }

      onScroll() {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          const width = this.pageStep();
          if (!width) return;
          const index = Math.round(this.track.scrollLeft / width);
          this.setActive(Math.max(0, Math.min(index, this.pages.length - 1)));
        });
      }

      setActive(index) {
        this.index = index;
        this.dashes.forEach((dash, i) => {
          const active = i === index;
          dash.classList.toggle('is-active', active);
          dash.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (this.prev) this.prev.disabled = index === 0;
        if (this.next) this.next.disabled = index === this.pages.length - 1;
      }

      /** Mark rows whose product already has units in the cart. */
      refreshCart() {
        const cartUrl = window.routes && window.routes.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
        fetch(cartUrl)
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (!cart || !cart.items) return;
            const counts = {};
            cart.items.forEach((item) => {
              counts[item.product_id] = (counts[item.product_id] || 0) + item.quantity;
            });
            const hideInCart = this.hasAttribute('data-hide-in-cart');
            this.querySelectorAll('.addon-list__item').forEach((row) => {
              const id = Number(row.dataset.productId);
              if (hideInCart && counts[id]) {
                row.remove();
                return;
              }
              this.markRow(row, counts[id]);
            });
            // A cart-side carousel drops rows that land in the cart; once it
            // has nothing left to offer, the whole block goes
            if (hideInCart && !this.querySelector('.addon-list__item')) {
              (this.closest('[data-carousel-host]') || this).remove();
            }
          })
          .catch(() => {});
      }

      /**
       * Show a cart button on rows already in the cart. Clicking it opens the
       * cart drawer when the theme has one, otherwise it goes to the cart page.
       */
      markRow(row, count) {
        const actions = row.querySelector('.quick-add');
        if (!actions) return;
        const existing = actions.querySelector('.addon-cart-btn');

        if (!count) {
          if (existing) existing.remove();
          return;
        }

        const label = `${count} in cart. View cart`;

        if (existing) {
          existing.querySelector('.addon-cart-btn__count').textContent = count;
          existing.setAttribute('aria-label', label);
          return;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'addon-cart-btn';
        button.setAttribute('aria-label', label);
        button.title = label;
        button.innerHTML =
          '<svg aria-hidden="true" focusable="false" class="icon" viewBox="0 0 24 24" fill="none">' +
          '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6Z"/>' +
          '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" d="M3 6h18"/>' +
          '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" d="M16 10a4 4 0 0 1-8 0"/>' +
          '</svg><span class="addon-cart-btn__count">' + count + '</span>';
        button.addEventListener('click', this.openCart.bind(this));
        actions.appendChild(button);
      }

      openCart(event) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('is-empty')) {
          event.preventDefault();
          drawer.open(event.currentTarget);
          return;
        }
        window.location.href = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
      }
    }
  );
}
